import { useState, useCallback } from 'react';
import api from '@/services/api/client';
import toast from 'react-hot-toast';
import { useTranslation } from '@/store/languageStore';
import { filterFlinkeyBoxList } from '@/utils/flinkeyBoxListFilter';

export interface FlinkeyBox {
  id: number;
  product_id: string;
  serial_number: string;
  box_type: string | null;
  car_name: string | null;
  license_plate: string | null;
  contract_start: string | null;
  contract_state: string | null;
  vehicle_id: number | null;
  organization_id: number;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
  firmware_version?: string | null;
  deviaq_row_json?: Record<string, unknown> | null;
  vehicle_registration?: string;
  vehicle_name?: string;
  user_count?: number;
  // NFC/test-läge + permanent ström
  nfc_check_enabled?: boolean;
  nfc_card_check_enabled?: boolean;
  nfc_detect_enabled?: boolean;
  has_permanent_power?: boolean;
  // Batteristatus (sista kända + härlett)
  last_battery_pct?: number | null;
  last_battery_charging?: boolean | null;
  last_charger_connected?: boolean | null;
  battery_updated_at?: string | null;
  battery_status?: 'ok' | 'warning' | 'critical' | 'unknown';
  charger_warning?: boolean;
  /** 1 om användaren är ansvarig på en pool där fordonet ligger. */
  responsible_for_me?: boolean | number;
}

export interface FlinkeyNfcTag {
  id: number;
  box_id: number;
  nfc_uid: string;
  label: string | null;
  is_required: boolean;
  registered_by: string | null;
  registered_at: string;
}

export interface FlinkeyBoxUser {
  id: number;
  box_id: number;
  user_id: string;
  access_type: 'permanent' | 'booking';
  valid_from: string | null;
  valid_until: string | null;
  granted_by_user_id: string | null;
  created_at: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface BoxFormData {
  product_id: string;
  serial_number: string;
  box_type: string;
  car_name: string;
  license_plate: string;
  contract_start: string;
  contract_state: string;
  notes: string;
  nfc_check_enabled: boolean;
  nfc_card_check_enabled: boolean;
  nfc_detect_enabled: boolean;
  has_permanent_power: boolean;
}

const emptyForm = (): BoxFormData => ({
  product_id: '',
  serial_number: '',
  box_type: '',
  car_name: '',
  license_plate: '',
  contract_start: '',
  contract_state: '',
  notes: '',
  nfc_check_enabled: true,
  nfc_card_check_enabled: false,
  nfc_detect_enabled: false,
  has_permanent_power: false,
});

export function useFlinkeyBoxes() {
  const { t } = useTranslation();
  const [boxes, setBoxes] = useState<FlinkeyBox[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [onlyResponsible, setOnlyResponsible] = useState(false);
  const [listScope, setListScope] = useState<'all' | 'responsible'>('all');

  // Multi-select för bulk org-tilldelning
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showAssignOrg, setShowAssignOrg] = useState(false);

  // Modals
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [selected, setSelected] = useState<FlinkeyBox | null>(null);
  const [formData, setFormData] = useState<BoxFormData>(emptyForm());

  // Users modal
  const [boxUsers, setBoxUsers] = useState<FlinkeyBoxUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const fetchBoxes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/flinkey/boxes');
      setBoxes(res.data.data || []);
      setListScope(res.data.scope === 'responsible' ? 'responsible' : 'all');
    } catch {
      toast.error(t('flinkeyBoxes.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const openCreate = useCallback(() => {
    setFormData(emptyForm());
    setShowCreate(true);
  }, []);

  const openEdit = useCallback((box: FlinkeyBox) => {
    setSelected(box);
    setFormData({
      product_id: box.product_id,
      serial_number: box.serial_number,
      box_type: box.box_type ?? '',
      car_name: box.car_name ?? '',
      // Förfyll regnummer från kopplat fordon om Flinkey-fältet är tomt
      license_plate: box.license_plate ?? box.vehicle_registration ?? '',
      contract_start: box.contract_start ?? '',
      contract_state: box.contract_state ?? '',
      notes: box.notes ?? '',
      nfc_check_enabled: box.nfc_check_enabled ?? true,
      nfc_card_check_enabled: box.nfc_card_check_enabled ?? false,
      nfc_detect_enabled: box.nfc_detect_enabled ?? false,
      has_permanent_power: box.has_permanent_power ?? false,
    });
    setShowEdit(true);
  }, []);

  const openUsers = useCallback(async (box: FlinkeyBox) => {
    setSelected(box);
    setShowUsers(true);
    setLoadingUsers(true);
    try {
      const res = await api.get(`/api/admin/flinkey/boxes/${box.id}/users`);
      setBoxUsers(res.data.data || []);
    } catch {
      toast.error(t('flinkeyBoxes.usersError'));
    } finally {
      setLoadingUsers(false);
    }
  }, [t]);

  const handleCreate = useCallback(async () => {
    if (!formData.product_id.trim() || !formData.serial_number.trim()) {
      toast.error(t('flinkeyBoxes.requiredFields'));
      return;
    }
    try {
      await api.post('/api/admin/flinkey/boxes', formData);
      toast.success(t('flinkeyBoxes.createSuccess'));
      setShowCreate(false);
      fetchBoxes();
    } catch (e: any) {
      toast.error(e.response?.data?.error || t('flinkeyBoxes.saveError'));
    }
  }, [formData, t, fetchBoxes]);

  const handleUpdate = useCallback(async () => {
    if (!selected) return;
    try {
      await api.put(`/api/admin/flinkey/boxes/${selected.id}`, formData);
      toast.success(t('flinkeyBoxes.updateSuccess'));
      setShowEdit(false);
      fetchBoxes();
    } catch (e: any) {
      toast.error(e.response?.data?.error || t('flinkeyBoxes.saveError'));
    }
  }, [selected, formData, t, fetchBoxes]);

  const handleDelete = useCallback(async (box: FlinkeyBox) => {
    try {
      await api.delete(`/api/admin/flinkey/boxes/${box.id}`);
      toast.success(t('flinkeyBoxes.deleteSuccess'));
      fetchBoxes();
    } catch (e: any) {
      toast.error(e.response?.data?.error || t('flinkeyBoxes.deleteError'));
    }
  }, [t, fetchBoxes]);

  const linkVehicle = useCallback(async (boxId: number, vehicleId: number) => {
    await api.post(`/api/admin/flinkey/boxes/${boxId}/vehicle`, { vehicleId });
    fetchBoxes();
  }, [fetchBoxes]);

  const unlinkVehicle = useCallback(async (boxId: number) => {
    await api.delete(`/api/admin/flinkey/boxes/${boxId}/vehicle`);
    fetchBoxes();
  }, [fetchBoxes]);

  const addUser = useCallback(async (boxId: number, userId: string, validFrom?: string, validUntil?: string) => {
      await api.post(`/api/admin/flinkey/boxes/${boxId}/users`, { userId, validFrom: validFrom || undefined, validUntil: validUntil || undefined });
    toast.success(t('flinkeyBoxes.userAdded'));
    if (selected?.id === boxId) {
      const res = await api.get(`/api/admin/flinkey/boxes/${boxId}/users`);
      setBoxUsers(res.data.data || []);
    }
    fetchBoxes();
  }, [t, selected, fetchBoxes]);

  const removeUser = useCallback(async (boxId: number, userId: string) => {
    await api.delete(`/api/admin/flinkey/boxes/${boxId}/users/${userId}`);
    toast.success(t('flinkeyBoxes.userRemoved'));
    setBoxUsers(prev => prev.filter(u => u.user_id !== userId));
    fetchBoxes();
  }, [t, fetchBoxes]);

  const toggleSelect = useCallback((id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback((ids: number[]) => {
    setSelectedIds(prev => {
      if (ids.every(id => prev.has(id))) return new Set();
      return new Set(ids);
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const importDeviaq = useCallback(
    async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post<{
        success: boolean;
        data?: { created?: number; updated?: number };
        error?: string;
      }>('/api/admin/flinkey/boxes/import-deviaq', fd);
      if (!res.data.success) {
        throw new Error(res.data.error || t('flinkeyBoxes.importDeviaqError'));
      }
      const created = res.data.data?.created ?? 0;
      const updated = res.data.data?.updated ?? 0;
      toast.success(t('flinkeyBoxes.importDeviaqSuccess', { inserted: created, updated }));
      await fetchBoxes();
    },
    [t, fetchBoxes]
  );

  const getNfcTags = useCallback(async (boxId: number): Promise<FlinkeyNfcTag[]> => {
    const res = await api.get(`/api/admin/flinkey/boxes/${boxId}/nfc-tags`);
    return res.data.data || [];
  }, []);

  const deleteNfcTag = useCallback(async (boxId: number, tagId: number) => {
    await api.delete(`/api/admin/flinkey/boxes/${boxId}/nfc-tags/${tagId}`);
  }, []);

  const assignOrganization = useCallback(async (organizationId: number) => {
    const boxIds = Array.from(selectedIds);
    if (boxIds.length === 0 || !organizationId) return;
    try {
      await api.patch('/api/admin/flinkey/boxes/assign-org', { boxIds, organizationId });
      toast.success(t('flinkeyBoxes.assignOrgSuccess') || `${boxIds.length} box(ar) tilldelade`);
      setShowAssignOrg(false);
      setSelectedIds(new Set());
      fetchBoxes();
    } catch (e: any) {
      toast.error(e.response?.data?.error || t('flinkeyBoxes.saveError'));
    }
  }, [selectedIds, t, fetchBoxes]);

  const filteredBoxes = filterFlinkeyBoxList(boxes, { search, onlyResponsible });

  return {
    boxes: filteredBoxes,
    allBoxes: boxes,
    loading,
    search, setSearch,
    onlyResponsible, setOnlyResponsible,
    listScope,
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    showAssignOrg, setShowAssignOrg,
    assignOrganization,
    showCreate, setShowCreate,
    showEdit, setShowEdit,
    showUsers, setShowUsers,
    selected,
    formData, setFormData,
    boxUsers,
    loadingUsers,
    fetchBoxes,
    openCreate,
    openEdit,
    openUsers,
    handleCreate,
    handleUpdate,
    handleDelete,
    linkVehicle,
    unlinkVehicle,
    addUser,
    removeUser,
    importDeviaq,
    getNfcTags,
    deleteNfcTag,
  };
}
